/**
 * @fileoverview Business Logic (BL) for the "Cronómetro" game dynamic (Tile ID 50) of Club Atlético de San Luis (ADSL).
 * Encapsulates security validations (Anti-SQLi via Security.js), data sanitization and normalization
 * (10-digit WhatsApp phone, lowercase emails), official Mexico City time conversions (CST / America/Mexico_City),
 * Dual Telemetry dispatch (Google Tag Manager window.dataLayer + non-blocking DL.TrackMetric calls),
 * and local session state lifecycle in localStorage ('cronometer_user').
 * 
 * @module utilities/BL/Cronometer
 */

import * as DL from '../DL/Cronometer.js';
import { ValidateSqlInjection } from './Security.js';

// =============================================================================
// CONSTANTS & SESSION STORAGE KEYS
// =============================================================================

export const STORAGE_KEY_CRONOMETER_USER = 'cronometer_user';
export const STORAGE_KEY_SESSION_ID = 'cronometer_session_id';

// =============================================================================
// MEXICO TIME ZONE FORMATTING (America/Mexico_City / CST)
// =============================================================================

/**
 * Formats a date or timestamp string to Mexico City official local time (CST).
 * 
 * @function FormatToMexicoTime
 * @param {string|Date|number} [dateInput=new Date()] - The date or timestamp to format.
 * @param {Object} [formatOptions={}] - Custom options for Intl.DateTimeFormat.
 * @returns {string} Formatted date string in CST (e.g. '10/10/2026 18:30:00').
 */
export const FormatToMexicoTime = (dateInput = new Date(), formatOptions = {}) => {
    try {
        if (!dateInput) return '';
        const parsedDate = new Date(dateInput);
        if (isNaN(parsedDate.getTime())) return '';

        const defaultOptions = {
            timeZone: 'America/Mexico_City',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            ...formatOptions
        };

        return new Intl.DateTimeFormat('es-MX', defaultOptions).format(parsedDate);
    } catch (error) {
        return '';
    }
};

/**
 * Formats a date to ISO string with local Mexico City timezone representation.
 * 
 * @function GetMexicoLocalIsoString
 * @param {string|Date|number} [dateInput=new Date()]
 * @returns {string}
 */
export const GetMexicoLocalIsoString = (dateInput = new Date()) => {
    try {
        const date = new Date(dateInput);
        if (isNaN(date.getTime())) return '';
        return FormatToMexicoTime(date, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    } catch {
        return '';
    }
};

// =============================================================================
// DUAL TELEMETRY: GTM DATA LAYER + ASYNC DATABASE TRACKING
// =============================================================================

/**
 * Dispatches a dual telemetry event without blocking the user interface:
 * 1. Appends an event object with prefix 'cronometer_*' and CST timestamp to window.dataLayer.
 * 2. Fires an asynchronous, non-blocking HTTP request to DL.TrackMetric for database persistence.
 * 
 * @function TrackDynamicEvent
 * @param {string} eventName - Metric name (e.g. 'qr_scan', 'form_complete', 'game_stopped').
 * @param {Object} [params={}] - Additional context attributes.
 */
export const TrackDynamicEvent = (eventName, params = {}) => {
    try {
        if (!eventName || typeof eventName !== 'string') return;

        // Ensure uniform 'cronometer_' prefix for Google Tag Manager & GA4
        const normalizedEvent = eventName.toLowerCase().startsWith('cronometer_')
            ? eventName.toLowerCase()
            : `cronometer_${eventName.toLowerCase()}`;

        const timestampIso = new Date().toISOString();
        const timestampMexico = FormatToMexicoTime(new Date());

        // 1. GTM / GA4 window.dataLayer push
        if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: normalizedEvent,
                timestamp: timestampIso,
                mexicoTime: timestampMexico,
                ...params
            });
        }

        // 2. Resolve or generate active SessionId
        let sessionId = params.sessionId || params.connectionId;
        if (!sessionId && typeof window !== 'undefined' && window.localStorage) {
            sessionId = window.localStorage.getItem(STORAGE_KEY_SESSION_ID);
            if (!sessionId) {
                sessionId = `crono_sess_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
                window.localStorage.setItem(STORAGE_KEY_SESSION_ID, sessionId);
            }
        }

        // 3. Fire non-blocking asynchronous call to DL.TrackMetric
        DL.TrackMetric({
            eventName: normalizedEvent,
            sessionId: String(sessionId || 'anonymous'),
            source: params.source || 'adsl-website-fan',
            additionalDataJson: JSON.stringify({
                ...params,
                clientTimestamp: timestampIso,
                mexicoTime: timestampMexico
            })
        }).catch(() => {
            // Silently swallow network errors so game UI is never interrupted
        });
    } catch (err) {
        // Defensive safeguard against unexpected browser crashes
    }
};

// Convenience telemetry event dispatchers
export const TrackQrScan = (trafficSource = 'qr_estadio') => {
    TrackDynamicEvent('qr_scan', { trafficSource, source: 'mobile_qr' });
};

export const TrackPageView = (pageName = '/cronometro') => {
    TrackDynamicEvent('page_view', { page: pageName, source: 'mobile_page' });
};

export const TrackFormStart = () => {
    TrackDynamicEvent('form_start', { source: 'registration_form' });
};

export const TrackFormComplete = (participantId, isAbonado = false) => {
    TrackDynamicEvent('form_complete', { participantId, isAbonado, source: 'registration_form' });
};

export const TrackGameStart = (fase, targetZ, margin) => {
    TrackDynamicEvent('game_start', { fase, targetZ, margin, source: 'gameplay' });
};

export const TrackGameStopped = (fase, time, diff, isClassified) => {
    TrackDynamicEvent('game_stopped', { fase, time, diff, isClassified, source: 'gameplay' });
};

export const TrackClassified = (fase, time) => {
    TrackDynamicEvent('classified', { fase, time, source: 'gameplay' });
};

export const TrackEliminated = (fase, reason = 'out_of_margin') => {
    TrackDynamicEvent('eliminated', { fase, reason, source: 'gameplay' });
};

export const TrackDisconnect = (participantId = null) => {
    TrackDynamicEvent('disconnect', { participantId, source: 'websocket' });
};

// =============================================================================
// SESSION LIFECYCLE MANAGEMENT (localStorage: 'cronometer_user')
// =============================================================================

/**
 * Saves or updates participant session data in localStorage.
 * 
 * @function SaveSession
 * @param {Object} sessionData - Participant details and game achievements.
 * @returns {Object|null} The stored session object, or null on error.
 */
export const SaveSession = (sessionData) => {
    try {
        if (!sessionData || typeof sessionData !== 'object') return null;

        const currentSession = GetSession() || {};
        const mergedSession = {
            ...currentSession,
            ...sessionData,
            wonPhases: sessionData.wonPhases !== undefined
                ? sessionData.wonPhases
                : (currentSession.wonPhases || {}),
            phaseReached: sessionData.phaseReached !== undefined
                ? sessionData.phaseReached
                : (currentSession.phaseReached || 1),
            isWinner: sessionData.isWinner !== undefined
                ? sessionData.isWinner
                : (currentSession.isWinner || false),
            lastActiveAt: new Date().toISOString()
        };

        if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem(STORAGE_KEY_CRONOMETER_USER, JSON.stringify(mergedSession));
        }

        return mergedSession;
    } catch (error) {
        return null;
    }
};

/**
 * Retrieves the current participant session from localStorage.
 * 
 * @function GetSession
 * @returns {Object|null} Parsed session object, or null if empty or invalid.
 */
export const GetSession = () => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return null;
        const serialized = window.localStorage.getItem(STORAGE_KEY_CRONOMETER_USER);
        if (!serialized) return null;

        const parsed = JSON.parse(serialized);
        if (!parsed || typeof parsed !== 'object') return null;

        return parsed;
    } catch (error) {
        return null;
    }
};

/**
 * Clears participant session data from localStorage.
 * 
 * @function ClearSession
 */
export const ClearSession = () => {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.removeItem(STORAGE_KEY_CRONOMETER_USER);
        }
    } catch (error) {
        // Ignore storage exceptions
    }
};

/**
 * Updates phase progression and qualification status inside the stored session.
 * 
 * @function UpdateSessionPhases
 * @param {number} fase - Phase number (1, 2, 3, etc.).
 * @param {boolean} isClassified - Whether participant hit the target within margin.
 * @param {number} [timeAchieved=0] - Time achieved in seconds.
 * @returns {Object|null} Updated session object.
 */
export const UpdateSessionPhases = (fase, isClassified, timeAchieved = 0) => {
    try {
        const session = GetSession() || {};
        const wonPhases = { ...(session.wonPhases || {}) };
        const phaseNum = Number(fase);

        wonPhases[phaseNum] = Boolean(isClassified);

        const currentReached = Number(session.phaseReached) || 1;
        const newPhaseReached = isClassified ? Math.max(currentReached, phaseNum + 1) : currentReached;

        const updated = {
            ...session,
            wonPhases,
            phaseReached: newPhaseReached,
            lastTimeAchieved: Number(timeAchieved) || 0,
            lastActiveAt: new Date().toISOString()
        };

        return SaveSession(updated);
    } catch (error) {
        return null;
    }
};

// =============================================================================
// INPUT VALIDATION & SANITIZATION HELPERS
// =============================================================================

/**
 * Validates and normalizes an email address (trims, converts to lowercase, tests format, checks SQLi).
 * 
 * @async
 * @function NormalizeAndValidateEmail
 * @param {string} emailInput - Raw email input.
 * @returns {Promise<{isValid: boolean, email: string, message?: string}>}
 */
export const NormalizeAndValidateEmail = async (emailInput) => {
    if (!emailInput || typeof emailInput !== 'string' || emailInput.trim() === '') {
        return { isValid: false, email: '', message: 'El correo electrónico es obligatorio.' };
    }

    const normalizedEmail = emailInput.trim().toLowerCase();

    if (normalizedEmail.length > 150) {
        return { isValid: false, email: normalizedEmail, message: 'El correo electrónico no puede exceder 150 caracteres.' };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
        return { isValid: false, email: normalizedEmail, message: 'El formato de correo electrónico es inválido.' };
    }

    const isThreat = await ValidateSqlInjection(normalizedEmail);
    if (isThreat) {
        return { isValid: false, email: normalizedEmail, message: 'Se detectó un patrón inseguro en el correo electrónico.' };
    }

    return { isValid: true, email: normalizedEmail };
};

/**
 * Validates and normalizes a phone number to exactly 10 digits (WhatsApp standard).
 * Handles country prefixes (e.g. +52) and strips non-numeric characters.
 * 
 * @async
 * @function NormalizeAndValidatePhone
 * @param {string} phoneInput - Raw phone input.
 * @returns {Promise<{isValid: boolean, phone: string, message?: string}>}
 */
export const NormalizeAndValidatePhone = async (phoneInput) => {
    if (!phoneInput || (typeof phoneInput !== 'string' && typeof phoneInput !== 'number')) {
        return { isValid: false, phone: '', message: 'El número de teléfono celular es obligatorio.' };
    }

    const stringPhone = String(phoneInput).trim();

    // Check anti-SQLi first
    const isThreat = await ValidateSqlInjection(stringPhone);
    if (isThreat) {
        return { isValid: false, phone: '', message: 'Se detectó un patrón inseguro en el número telefónico.' };
    }

    // Strip all non-digit characters
    let cleanedDigits = stringPhone.replace(/\D/g, '');

    // Handle Mexico international prefix +52 (12 digits starting with 52)
    if (cleanedDigits.length === 12 && cleanedDigits.startsWith('52')) {
        cleanedDigits = cleanedDigits.slice(2);
    }

    // Validate strictly 10 numeric digits
    if (!/^\d{10}$/.test(cleanedDigits)) {
        return {
            isValid: false,
            phone: cleanedDigits,
            message: 'El teléfono debe contener exactamente 10 dígitos numéricos (ej. 4441234567).'
        };
    }

    return { isValid: true, phone: cleanedDigits };
};

/**
 * Validates a text field against length constraints and SQL injection patterns.
 * 
 * @async
 * @function SanitizeTextInput
 * @param {string} text - Input text.
 * @param {string} fieldName - Descriptive name for error messages.
 * @param {number} [maxLength=100] - Maximum allowed length.
 * @param {boolean} [isRequired=true] - Whether the field is mandatory.
 * @returns {Promise<{isValid: boolean, sanitized: string, message?: string}>}
 */
export const SanitizeTextInput = async (text, fieldName, maxLength = 100, isRequired = true) => {
    if (text === null || text === undefined || typeof text !== 'string') {
        if (isRequired) {
            return { isValid: false, sanitized: '', message: `El campo '${fieldName}' es obligatorio.` };
        }
        return { isValid: true, sanitized: '' };
    }

    const trimmed = text.trim();

    if (isRequired && trimmed === '') {
        return { isValid: false, sanitized: '', message: `El campo '${fieldName}' es obligatorio.` };
    }

    if (trimmed.length > maxLength) {
        return {
            isValid: false,
            sanitized: trimmed,
            message: `El campo '${fieldName}' no puede exceder ${maxLength} caracteres.`
        };
    }

    const isThreat = await ValidateSqlInjection(trimmed);
    if (isThreat) {
        return {
            isValid: false,
            sanitized: trimmed,
            message: `Se detectaron caracteres o patrones inseguros en '${fieldName}'.`
        };
    }

    return { isValid: true, sanitized: trimmed };
};

// =============================================================================
// BUSINESS LOGIC: REGISTRATION & PARTICIPANT VALIDATION
// =============================================================================

/**
 * Validates fan registration data, applies anti-SQLi and normalization,
 * emits dual telemetry ('form_complete'), and calls DL.RegisterParticipant.
 * On success, persists the returned session in localStorage.
 * 
 * @async
 * @function RegisterParticipantBL
 * @param {Object} rawData - User registration form data.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const RegisterParticipantBL = async (rawData) => {
    try {
        if (!rawData || typeof rawData !== 'object') {
            return { success: false, message: 'La información de registro es obligatoria.' };
        }

        // 1. Validate full name
        const nameValidation = await SanitizeTextInput(rawData.name, 'Nombre completo', 100, true);
        if (!nameValidation.isValid) {
            return { success: false, message: nameValidation.message };
        }

        // 2. Validate & normalize email
        const emailValidation = await NormalizeAndValidateEmail(rawData.email);
        if (!emailValidation.isValid) {
            return { success: false, message: emailValidation.message };
        }

        // 3. Validate & normalize WhatsApp phone
        const phoneValidation = await NormalizeAndValidatePhone(rawData.phone);
        if (!phoneValidation.isValid) {
            return { success: false, message: phoneValidation.message };
        }

        // 4. Validate terms acceptance
        if (!rawData.acceptedTerms) {
            return { success: false, message: 'Debe aceptar los Términos y Condiciones para participar.' };
        }

        // 5. Normalize Abonado choice
        const isAbonadoChoice = (rawData.abonado === 'Sí' || rawData.abonado === 'si' || rawData.abonado === true || rawData.isAbonado === true);
        const normalizedAbonado = isAbonadoChoice ? 'Sí' : 'No';

        const sanitizedPayload = {
            name: nameValidation.sanitized,
            email: emailValidation.email,
            phone: phoneValidation.phone,
            abonado: normalizedAbonado,
            acceptedTerms: true,
            acceptedMarketing: Boolean(rawData.acceptedMarketing),
            trafficSource: rawData.trafficSource || 'qr_estadio'
        };

        // 6. Invoke Data Layer
        const response = await DL.RegisterParticipant(sanitizedPayload);

        if (response.success && response.participantId) {
            // 7. Store participant session in localStorage
            SaveSession({
                participantId: response.participantId,
                name: sanitizedPayload.name,
                email: sanitizedPayload.email,
                phone: sanitizedPayload.phone,
                isAbonado: isAbonadoChoice,
                wonPhases: {},
                phaseReached: response.phaseReached || 1,
                isWinner: Boolean(response.isWinner)
            });

            // 8. Dual Telemetry dispatch
            TrackFormComplete(response.participantId, isAbonadoChoice);
        }

        return response;
    } catch (error) {
        return {
            success: false,
            message: error.message || 'Error al procesar el registro del participante.'
        };
    }
};

// =============================================================================
// BUSINESS LOGIC: THEMES MANAGEMENT
// =============================================================================

export const GetThemesBL = async () => {
    try {
        return await DL.GetThemes();
    } catch (error) {
        return { success: false, message: error.message || 'Error al consultar temas visuales.' };
    }
};

export const GetThemeByIdBL = async (themeId) => {
    try {
        const parsedId = Number(themeId);
        if (isNaN(parsedId) || parsedId <= 0) {
            return { success: false, message: 'Identificador de tema inválido.' };
        }
        return await DL.GetThemeById(parsedId);
    } catch (error) {
        return { success: false, message: error.message || 'Error al consultar el tema.' };
    }
};

/**
 * Validates theme properties (anti-SQLi, length, colors) and saves via DL.
 * 
 * @async
 * @function SaveThemeBL
 * @param {Object} themeData
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const SaveThemeBL = async (themeData) => {
    try {
        if (!themeData || typeof themeData !== 'object') {
            return { success: false, message: 'Los datos del tema son obligatorios.' };
        }

        const nameValidation = await SanitizeTextInput(themeData.name, 'Nombre del tema', 100, true);
        if (!nameValidation.isValid) {
            return { success: false, message: nameValidation.message };
        }

        if (themeData.coverTitle) {
            const coverValidation = await SanitizeTextInput(themeData.coverTitle, 'Título de portada', 150, false);
            if (!coverValidation.isValid) {
                return { success: false, message: coverValidation.message };
            }
        }

        if (themeData.id && Number(themeData.id) > 0) {
            return await DL.UpdateTheme(themeData.id, themeData);
        } else {
            return await DL.CreateTheme(themeData);
        }
    } catch (error) {
        return { success: false, message: error.message || 'Error al guardar el tema.' };
    }
};

export const DeleteThemeBL = async (themeId) => {
    try {
        const parsedId = Number(themeId);
        if (isNaN(parsedId) || parsedId <= 0) {
            return { success: false, message: 'Identificador de tema inválido.' };
        }
        return await DL.DeleteTheme(parsedId);
    } catch (error) {
        return { success: false, message: error.message || 'Error al eliminar el tema.' };
    }
};

/**
 * Validates file constraints (type, size <= 15MB) and uploads asset via DL.
 * 
 * @async
 * @function UploadThemeAssetBL
 * @param {File|Blob} file
 * @returns {Promise<{success: boolean, url?: string, message?: string}>}
 */
export const UploadThemeAssetBL = async (file) => {
    try {
        if (!file) {
            return { success: false, message: 'Debe seleccionar un archivo.' };
        }

        // Limit file size to 15MB
        const maxSizeBytes = 15 * 1024 * 1024;
        if (file.size && file.size > maxSizeBytes) {
            return { success: false, message: 'El archivo excede el tamaño máximo permitido de 15 MB.' };
        }

        return await DL.UploadThemeAsset(file);
    } catch (error) {
        return { success: false, message: error.message || 'Error al procesar el archivo.' };
    }
};

export const SetActiveThemeBL = async (themeId) => {
    try {
        const parsedId = Number(themeId);
        if (isNaN(parsedId) || parsedId <= 0) {
            return { success: false, message: 'Identificador de tema inválido.' };
        }
        return await DL.SetActiveTheme(parsedId);
    } catch (error) {
        return { success: false, message: error.message || 'Error al activar el tema.' };
    }
};

// =============================================================================
// BUSINESS LOGIC: ADMIN CONFIGURATION, RECORDS & STATS
// =============================================================================

export const UpdateAdminConfigBL = async (configData) => {
    try {
        if (!configData || typeof configData !== 'object') {
            return { success: false, message: 'La configuración es obligatoria.' };
        }

        // Validate targetZ and margin tolerances
        if (configData.targetZ !== undefined && (Number(configData.targetZ) < 1 || Number(configData.targetZ) > 60)) {
            return { success: false, message: 'El tiempo objetivo Z debe estar entre 1 y 60 segundos.' };
        }

        if (configData.marginTolerance !== undefined && (Number(configData.marginTolerance) <= 0 || Number(configData.marginTolerance) > 5.0)) {
            return { success: false, message: 'El margen de tolerancia debe ser mayor a 0 y menor a 5 segundos.' };
        }

        return await DL.UpdateAdminConfig(configData);
    } catch (error) {
        return { success: false, message: error.message || 'Error al actualizar la configuración.' };
    }
};

export const GetAdminRecordsBL = async (search = '', filter = '') => {
    try {
        if (search) {
            const isThreat = await ValidateSqlInjection(search);
            if (isThreat) {
                return { success: false, message: 'Búsqueda contiene caracteres no permitidos.' };
            }
        }
        return await DL.GetAdminRecords(search, filter);
    } catch (error) {
        return { success: false, message: error.message || 'Error al consultar registros de participantes.' };
    }
};

export const GetConnectionStatsBL = async () => {
    try {
        return await DL.GetConnectionStats();
    } catch (error) {
        return { success: false, message: error.message || 'Error al consultar métricas de conexión.' };
    }
};

export const GetAdminStatsBL = async () => {
    try {
        return await DL.GetAdminStats();
    } catch (error) {
        return { success: false, message: error.message || 'Error al consultar estadísticas del embudo.' };
    }
};

export const ResetGameBL = async () => {
    try {
        return await DL.ResetGame();
    } catch (error) {
        return { success: false, message: error.message || 'Error al reiniciar la dinámica.' };
    }
};

export const ExportExcelReportBL = async () => {
    try {
        return await DL.ExportExcelReport();
    } catch (error) {
        return { success: false, message: error.message || 'Error al generar el reporte en Excel.' };
    }
};

export const PurgeAllDynamicDataBL = async (masterPassword, operatorName = 'Admin') => {
    try {
        const validMasterPasswords = ['evitarborrarregistros456852'];
        const trimmed = masterPassword ? masterPassword.trim() : '';
        if (!validMasterPasswords.includes(trimmed)) {
            return {
                success: false,
                message: 'Contraseña maestra incorrecta. No tienes autorización para reiniciar la dinámica.'
            };
        }

        return await DL.PurgeAllDynamicData(trimmed, operatorName);
    } catch (error) {
        return {
            success: false,
            message: error.message || 'Error al ejecutar el reinicio total de la dinámica.'
        };
    }
};

export default {
    FormatToMexicoTime,
    GetMexicoLocalIsoString,
    TrackDynamicEvent,
    TrackQrScan,
    TrackPageView,
    TrackFormStart,
    TrackFormComplete,
    TrackGameStart,
    TrackGameStopped,
    TrackClassified,
    TrackEliminated,
    TrackDisconnect,
    SaveSession,
    GetSession,
    ClearSession,
    UpdateSessionPhases,
    NormalizeAndValidateEmail,
    NormalizeAndValidatePhone,
    SanitizeTextInput,
    RegisterParticipantBL,
    GetThemesBL,
    GetThemeByIdBL,
    SaveThemeBL,
    DeleteThemeBL,
    UploadThemeAssetBL,
    SetActiveThemeBL,
    UpdateAdminConfigBL,
    GetAdminRecordsBL,
    GetConnectionStatsBL,
    GetAdminStatsBL,
    ResetGameBL,
    PurgeAllDynamicDataBL,
    ExportExcelReportBL
};
